import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { SocialLinks } from "@/components/Social";
import { CompletaProfilo } from "@/components/CompletaProfilo";
import { InstallApp } from "@/components/InstallApp";

const NAV = [
  ["/", "Home"],
  ["/notizie", "News"],
  ["/profilo", "Profilo"],
  ["/tessera", "Tessera"],
  ["/barche", "Barche"],
  ["/regate", "Regate"],
  ["/mercatino", "Mercatino"],
];

const ROLE_LABEL = { admin: "Amministratore", utente: "Utente", collaboratore: "Collaboratore" };

const NavItems = ({ links, onClick, mobile }) =>
  links.map(([to, label]) => (
    <NavLink
      key={to}
      to={to}
      end={to === "/"}
      onClick={onClick}
      data-testid={`nav-${label.toLowerCase()}-link${mobile ? "-mobile" : ""}`}
      className={({ isActive }) =>
        `font-heading uppercase tracking-wider text-[15px] px-3 py-2 rounded-md transition-colors duration-200 ${
          isActive ? "bg-white/10 text-white" : "text-slate-300 hover:text-white hover:bg-white/5"
        }`
      }
    >
      {label}
    </NavLink>
  ));

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const links = user.role === "admin" ? [...NAV, ["/admin", "Admin"]] : user.role === "collaboratore" ? [...NAV, ["/admin", "Gestione"]] : NAV;

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen">
      <CompletaProfilo />
      <header className="sticky top-0 z-50 bg-[#0B192C]/95 backdrop-blur-xl text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo-link">
            <img src="/logo-9er.png" alt="Logo 9er" className="h-10 w-10 bg-white rounded-md p-0.5" />
            <span className="font-heading text-lg uppercase tracking-wide leading-none hidden sm:block">
              Associazione italiana<br />Classi Nacra-9er
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-1 ml-4">
            <NavItems links={links} />
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <InstallApp className="hidden sm:inline-flex" />
            <NotificationBell />
            <div className="hidden md:block text-right leading-tight px-2" data-testid="header-user-info">
              <div className="text-sm font-medium">{user.nome} {user.cognome}</div>
              <div className="text-[11px] uppercase tracking-wider text-emerald-400">{ROLE_LABEL[user.role]}</div>
            </div>
            <button onClick={doLogout} className="p-2 rounded-md hover:bg-white/10 transition-colors" title="Esci" data-testid="logout-button">
              <LogOut className="h-5 w-5" />
            </button>
            <button onClick={() => setOpen(!open)} className="lg:hidden p-2 rounded-md hover:bg-white/10" data-testid="mobile-menu-button">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="lg:hidden flex flex-col px-4 pb-4 gap-1" data-testid="mobile-nav">
            <NavItems links={links} onClick={() => setOpen(false)} mobile />
          </nav>
        )}
        <div className="tricolore h-1" />
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">{children}</main>
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 text-xs text-muted-foreground flex flex-wrap items-center gap-4" data-testid="app-footer">
        <span>© {new Date().getFullYear()} Associazione italiana Classi Nacra-9er</span>
        <Link to="/comunicazioni" className="hover:underline" data-testid="footer-comunicazioni-link">Comunicazioni</Link>
        <Link to="/associazione" className="hover:underline" data-testid="footer-associazione-link">Chi siamo</Link>
        <Link to="/privacy" className="hover:underline" data-testid="footer-privacy-link">Privacy policy</Link>
        <span className="ml-auto"><SocialLinks size="h-8 w-8" /></span>
      </footer>
    </div>
  );
}
